import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SortUtilsService {

  constructor(private translate: TranslateService) {
  }

  public sortByTransId<T>(datasToSort: T[], transIdPropertyName: string, sortable?: SortableTransId<T>): void {
    this.getObservableSortByTransId(datasToSort, transIdPropertyName, sortable).subscribe();
  }

  public getObservableSortByTransId<T>(datasToSort: T[], transIdPropertyName: string, sortable?: SortableTransId<T>): Observable<T[]> {
    const datasNeededTransValue = this.computeNeededTransValues(datasToSort, transIdPropertyName);
    if (datasNeededTransValue?.length <= 0) {
      return of(datasToSort);
    }
    return of(datasToSort).pipe(
      switchMap((datas: T[]) => forkJoin(of(datas), this.translate.get(datasNeededTransValue))),
      map(([datas, translateValues]) => this.sort(datas, transIdPropertyName, translateValues, sortable))
    );
  }

  private computeNeededTransValues(datas: any[], transIdPropertyName: string): Array<string> {
    const neededTransIds: Array<string> = new Array<string>();
    if (datas) {
      datas.forEach(payType => {
        neededTransIds.push(payType[transIdPropertyName]);
      });
    }
    return neededTransIds;
  }

  private sort<T>(datas: any[], transIdPropertyName: string, translationValues, sortable: SortableTransId<T>): any[] {
    if (!datas) {
      return [];
    }
    const sortFn = !sortable ? new DefaultTransIdSort<T>().sort : sortable.sort;
    return datas.sort(sortFn(transIdPropertyName, translationValues));
  }
}

interface SortableTransId<T> {
  sort(transIdPropertyName: string, translationValues): (a: T, b: T) => number;
}

export class DefaultTransIdSort<T> implements SortableTransId<T> {
  sort = (transIdPropertyName: string, translationValues): (a: T, b: T) => number => {
    return (itemA, itemB) => this.defaultSort(itemA, itemB, transIdPropertyName, translationValues);
  }

  protected defaultSort(itemA: T, itemB: T, transIdPropertyName: string, translationValues): number {
    const itemATranslation = translationValues[itemA[transIdPropertyName]] || itemA[transIdPropertyName];
    const itemBTranslation = translationValues[itemB[transIdPropertyName]] || itemB[transIdPropertyName];
    return itemATranslation.localeCompare(itemBTranslation);
  }
}
